import { FormEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../../../../shared/components/Button";
import OrganizerDatePicker from "../../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput, {
  parseClockTimeToDate,
} from "../../../../shared/components/OrganizerTimeInput";
import Toast from "../../../../shared/components/Toast";
import UserAvatar from "../../../../shared/components/UserAvatar";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { Club } from "../../../clubs/shared/types/club";
import { listPublicPlayers } from "../../../players/shared/services/publicPlayerProfileService";
import type { PublicPlayerListItem } from "../../../players/shared/types/publicPlayerProfile";
import type {
  CreateOpenMatchPayload,
  OpenMatchInitialParticipant,
  OpenMatchItem,
} from "../../shared/types/matches";
import {
  buildOpenMatchDefaultTitle,
  formatOpenMatchPerPlayerPrice,
} from "../../shared/utils/openMatchPresentation";

type MatchCreatePanelProps = {
  open: boolean;
  clubs: Club[];
  loadingClubs: boolean;
  mode?: "create" | "edit";
  initialMatch?: OpenMatchItem | null;
  prefillClubId?: number | null;
  defaultOrganizerName?: string;
  onSubmit: (payload: CreateOpenMatchPayload) => Promise<void>;
  onCancelMatch?: () => Promise<void>;
  onAddParticipant?: (
    match: OpenMatchItem,
    payload: OpenMatchInitialParticipant,
  ) => Promise<void>;
  onReviewParticipant?: (
    participantId: number,
    status: "confirmed" | "declined" | "canceled",
  ) => Promise<void>;
  reviewingParticipantId?: number | null;
  onClose: () => void;
};

const CATEGORY_VALUES = [
  "7a",
  "6a",
  "5a",
  "4a",
  "3a",
  "2a",
  "open",
  "free",
] as const;
type InitialParticipantDraft = {
  id: string;
  participantType: "registered" | "external" | "placeholder";
  userId: number | null;
  participantName: string;
  participantCpf: string;
  avatarUrl: string;
  sex: "male" | "female" | "other" | "";
  categoryLabel: string;
};
type ExistingParticipantDisplay = {
  id: string;
  participantId: number | null;
  name: string;
  avatarUrl: string;
  sex: "male" | "female" | "other" | "";
  meta: string;
  status: string;
};

function categoryLabel(value: (typeof CATEGORY_VALUES)[number]) {
  if (value === "7a") return siteTexts.common.category.c7a;
  if (value === "6a") return siteTexts.common.category.c6a;
  if (value === "5a") return siteTexts.common.category.c5a;
  if (value === "4a") return siteTexts.common.category.c4a;
  if (value === "3a") return siteTexts.common.category.c3a;
  if (value === "2a") return siteTexts.common.category.c2a;
  if (value === "free") return siteTexts.common.category.free;
  return siteTexts.common.category.open;
}

function toCurrencyInput(valueInCents: number) {
  return (Number(valueInCents || 0) / 100).toFixed(2);
}

function formatMatchDurationLabel(minutes: number) {
  const totalMinutes = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (!hours) return `${remainingMinutes} min`;
  if (!remainingMinutes) return `${hours}h`;
  return `${hours}h ${remainingMinutes}min`;
}

function normalizeClubSearch(value: string) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function datePartFromStoredDateTime(value?: string | null) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})(?:T|\s)/);
  if (match) return match[1];
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function timePartFromStoredDateTime(value?: string | null) {
  const raw = String(value || "").trim();
  const match = raw.match(/(?:T|\s)(\d{2}:\d{2})/);
  if (match) return match[1];
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function ParticipantStatusIcon({
  status,
  label,
}: {
  status: string;
  label: string;
}) {
  if (status === "pending" || status === "invited") {
    return (
      <span
        className="match-create-panel__participant-status match-create-panel__participant-status--pending material-symbols-outlined"
        aria-label={label}
        title={label}
      >
        schedule
      </span>
    );
  }
  if (status === "confirmed") {
    return (
      <span
        className="match-create-panel__participant-status match-create-panel__participant-status--confirmed material-symbols-outlined"
        aria-label={label}
        title={label}
      >
        check_circle
      </span>
    );
  }
  return null;
}

export default function MatchCreatePanel({
  open,
  clubs,
  loadingClubs,
  mode = "create",
  initialMatch = null,
  prefillClubId = null,
  defaultOrganizerName = "",
  onSubmit,
  onCancelMatch,
  onAddParticipant,
  onReviewParticipant,
  reviewingParticipantId = null,
  onClose,
}: MatchCreatePanelProps) {
  const texts = siteTexts.openMatches;
  const [clubId, setClubId] = useState("");
  const [clubSearch, setClubSearch] = useState("");
  const [clubResultsOpen, setClubResultsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [totalPlayers, setTotalPlayers] = useState("4");
  const [categories, setCategories] = useState<string[]>(["5a"]);
  const [genderPreference, setGenderPreference] =
    useState<CreateOpenMatchPayload["genderPreference"]>("any");
  const [leftPlayersCount, setLeftPlayersCount] = useState("");
  const [rightPlayersCount, setRightPlayersCount] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [priceMode, setPriceMode] =
    useState<CreateOpenMatchPayload["priceMode"]>("court");
  const [paymentMode, setPaymentMode] =
    useState<CreateOpenMatchPayload["paymentMode"]>("organizer");
  const [details, setDetails] = useState("");
  const [organizerPlays, setOrganizerPlays] = useState(true);
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);
  const [initialParticipants, setInitialParticipants] = useState<
    InitialParticipantDraft[]
  >([]);
  const [participantTypeDraft, setParticipantTypeDraft] =
    useState<InitialParticipantDraft["participantType"]>("registered");
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantOptions, setParticipantOptions] = useState<PublicPlayerListItem[]>(
    [],
  );
  const [participantUserIdDraft, setParticipantUserIdDraft] = useState("");
  const [participantNameDraft, setParticipantNameDraft] = useState("");
  const [participantCpfDraft, setParticipantCpfDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "error">("success");
  const controlsDisabled = saving || canceling;

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialMatch) {
      setClubId(
        initialMatch.clubId === null || initialMatch.clubId === undefined
          ? ""
          : String(initialMatch.clubId),
      );
      setClubSearch(initialMatch.clubName || "");
      setClubResultsOpen(false);
      setTitle(initialMatch.title || buildOpenMatchDefaultTitle(initialMatch.organizer?.name));
      setDate(datePartFromStoredDateTime(initialMatch.startAt));
      setStartTime(timePartFromStoredDateTime(initialMatch.startAt));
      setEndTime(timePartFromStoredDateTime(initialMatch.endAt));
      setTotalPlayers(String(initialMatch.totalPlayers || 4));
      setCategories(initialMatch.categories?.length ? initialMatch.categories : ["5a"]);
      setGenderPreference(initialMatch.genderPreference || "any");
      setLeftPlayersCount(
        initialMatch.leftPlayersCount ? String(initialMatch.leftPlayersCount) : "",
      );
      setRightPlayersCount(
        initialMatch.rightPlayersCount ? String(initialMatch.rightPlayersCount) : "",
      );
      setPriceMode(initialMatch.priceMode || "court");
      setTotalPrice(
        toCurrencyInput(
          initialMatch.priceMode === "player"
            ? Math.round(
                Number(initialMatch.totalPriceCents || 0) /
                  Math.max(1, Number(initialMatch.totalPlayers || 1)),
              )
            : initialMatch.totalPriceCents || 0,
        ),
      );
      setPaymentMode(initialMatch.paymentMode || "organizer");
      setDetails(
        initialMatch.description ||
          initialMatch.notes ||
          initialMatch.paymentInstructions ||
          "",
      );
      setOrganizerPlays(Boolean(initialMatch.organizerPlays));
      setApprovalRequired(Boolean(initialMatch.approvalRequired));
      setResponsibilityAccepted(Boolean(initialMatch.responsibilityAccepted));
      setInitialParticipants([]);
      setParticipantTypeDraft("registered");
      setParticipantSearch("");
      setParticipantUserIdDraft("");
      setParticipantNameDraft("");
      setParticipantCpfDraft("");
      return;
    }

    setClubId(
      Number.isFinite(Number(prefillClubId)) && Number(prefillClubId) > 0
        ? String(prefillClubId)
        : "",
    );
    setClubSearch("");
    setClubResultsOpen(false);
    setTitle(buildOpenMatchDefaultTitle(defaultOrganizerName));
    setDate("");
    setStartTime("");
    setEndTime("");
    setTotalPlayers("4");
    setCategories(["5a"]);
    setGenderPreference("any");
    setLeftPlayersCount("");
    setRightPlayersCount("");
    setTotalPrice("");
    setPriceMode("court");
    setPaymentMode("organizer");
    setDetails("");
    setOrganizerPlays(true);
    setApprovalRequired(true);
    setResponsibilityAccepted(false);
    setInitialParticipants([]);
    setParticipantTypeDraft("registered");
    setParticipantSearch("");
    setParticipantUserIdDraft("");
    setParticipantNameDraft("");
    setParticipantCpfDraft("");
  }, [defaultOrganizerName, initialMatch, mode, open, prefillClubId]);

  const selectedClub = useMemo(
    () => clubs.find((club) => String(club.id) === clubId) || null,
    [clubId, clubs],
  );

  useEffect(() => {
    if (!open || !selectedClub) return;
    setClubSearch(selectedClub.name);
  }, [open, selectedClub]);

  useEffect(() => {
    if (!open || participantTypeDraft !== "registered") return;
    let cancelled = false;
    listPublicPlayers({ search: participantSearch })
      .then((response) => {
        if (!cancelled) setParticipantOptions(response.players || []);
      })
      .catch(() => {
        if (!cancelled) setParticipantOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, participantSearch, participantTypeDraft]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !controlsDisabled) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [controlsDisabled, onClose, open]);

  const estimatedPerPlayer = useMemo(() => {
    const totalPriceValue = Number(totalPrice || 0);
    const priceCents = Math.round(
      (Number.isFinite(totalPriceValue) ? totalPriceValue : 0) * 100,
    );
    const totalPriceCents =
      priceMode === "player"
        ? priceCents * Math.max(1, Number(totalPlayers || 1))
        : priceCents;
    if (totalPriceCents <= 0) return texts.form.pricePending;
    return formatOpenMatchPerPlayerPrice({
      totalPriceCents,
      totalPlayers: Number(totalPlayers || 1),
      priceMode,
    });
  }, [priceMode, texts.form.pricePending, totalPrice, totalPlayers]);

  const calculatedDuration = useMemo(() => {
    const start = parseClockTimeToDate(startTime);
    const end = parseClockTimeToDate(endTime);

    if (!startTime && !endTime) {
      return {
        invalid: false,
        label: texts.form.durationPending,
      };
    }

    if (!start || !end) {
      return {
        invalid: true,
        label: texts.form.durationInvalid,
      };
    }

    const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      return {
        invalid: true,
        label: texts.form.durationInvalid,
      };
    }

    return {
      invalid: false,
      label: formatMatchDurationLabel(durationMinutes),
    };
  }, [
    endTime,
    startTime,
    texts.form.durationInvalid,
    texts.form.durationPending,
  ]);

  const selectedParticipantOption = useMemo(
    () =>
      participantOptions.find(
        (participant) => String(participant.id) === participantUserIdDraft,
      ) || null,
    [participantOptions, participantUserIdDraft],
  );
  const filteredClubs = useMemo(() => {
    const query = normalizeClubSearch(clubSearch);
    const source = query
      ? clubs.filter((club) => {
          const searchable = normalizeClubSearch(
            [
              club.name,
              club.city,
              club.state,
              club.address,
              club.neighborhood,
            ].join(" "),
          );
          return searchable.includes(query);
        })
      : clubs;
    return source.slice(0, 12);
  }, [clubSearch, clubs]);
  const existingParticipants = useMemo<ExistingParticipantDisplay[]>(() => {
    if (mode !== "edit" || !initialMatch) return [];
    return [
      ...(initialMatch.organizerPlays
        ? [
            {
              id: "organizer",
              participantId: null,
              name: initialMatch.organizer.name,
              avatarUrl: initialMatch.organizer.avatarUrl || "",
              sex: "" as const,
              meta: texts.detail.organizerPlayer,
              status: "confirmed",
            },
          ]
        : []),
      ...((initialMatch.participants || [])
        .filter((participant) =>
          ["invited", "pending", "confirmed"].includes(
            participant.status,
          ),
        )
        .map((participant) => ({
          id: `participant-${participant.id}`,
          participantId: participant.id,
          name: participant.participantName || texts.detail.participantFallback,
          avatarUrl: participant.avatarUrl || "",
          sex:
            participant.sex === "male" ||
            participant.sex === "female" ||
            participant.sex === "other"
              ? participant.sex
              : ("" as const),
          meta: texts.detail.participantStatuses[participant.status],
          status: participant.status,
        }))),
    ];
  }, [
    initialMatch,
    mode,
    texts.detail.organizerPlayer,
    texts.detail.participantFallback,
    texts.detail.participantStatuses,
  ]);
  const unavailableParticipantUserIds = useMemo(
    () =>
      new Set(
        [
          ...(initialParticipants
            .map((participant) => participant.userId)
            .filter((userId): userId is number => Boolean(userId))),
          ...((initialMatch?.participants || [])
            .filter((participant) =>
              ["confirmed", "pending", "invited"].includes(participant.status),
            )
            .map((participant) => participant.userId)
            .filter((userId): userId is number => Boolean(userId))),
          initialMatch?.organizer?.id,
        ].filter((userId): userId is number => Boolean(userId)),
      ),
    [initialMatch?.organizer?.id, initialMatch?.participants, initialParticipants],
  );
  const availableParticipantOptions = useMemo(
    () =>
      participantOptions.filter(
        (participant) => !unavailableParticipantUserIds.has(participant.id),
      ),
    [participantOptions, unavailableParticipantUserIds],
  );

  useEffect(() => {
    if (
      participantUserIdDraft &&
      !availableParticipantOptions.some(
        (participant) => String(participant.id) === participantUserIdDraft,
      )
    ) {
      setParticipantUserIdDraft("");
    }
  }, [availableParticipantOptions, participantUserIdDraft]);

  const addParticipantDisabled =
    controlsDisabled ||
    (participantTypeDraft === "registered" && !selectedParticipantOption) ||
    (participantTypeDraft === "external" && !participantNameDraft.trim());
  const normalizedLeftPlayersCount = Math.max(0, Number(leftPlayersCount || 0));
  const normalizedRightPlayersCount = Math.max(0, Number(rightPlayersCount || 0));

  function toggleCategory(category: string) {
    setCategories((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category],
    );
  }

  function resetParticipantDraft() {
    setParticipantUserIdDraft("");
    setParticipantSearch("");
    setParticipantNameDraft("");
    setParticipantCpfDraft("");
  }

  function buildInitialParticipantDraft(): InitialParticipantDraft | undefined {
    if (participantTypeDraft === "registered" && !selectedParticipantOption) return;
    if (participantTypeDraft === "external" && !participantNameDraft.trim()) return;

    if (
      participantTypeDraft === "registered" &&
      initialParticipants.some(
        (participant) => participant.userId === Number(selectedParticipantOption?.id),
      )
    ) {
      setToastVariant("error");
      setToastMessage(texts.form.participantAlreadySelected);
      return;
    }

    return participantTypeDraft === "registered" && selectedParticipantOption
      ? {
          id: `${Date.now()}-${selectedParticipantOption.id}`,
          participantType: "registered" as const,
          userId: selectedParticipantOption.id,
          participantName: selectedParticipantOption.displayName,
          participantCpf: "",
          avatarUrl: selectedParticipantOption.avatarUrl || "",
          sex:
            selectedParticipantOption.sex === "male" ||
            selectedParticipantOption.sex === "female" ||
            selectedParticipantOption.sex === "other"
              ? selectedParticipantOption.sex
              : "",
          categoryLabel: selectedParticipantOption.categoryLabel || "",
        }
      : participantTypeDraft === "external"
        ? {
            id: `${Date.now()}-external`,
            participantType: "external" as const,
            userId: null,
            participantName: participantNameDraft.trim(),
            participantCpf: participantCpfDraft.trim(),
            avatarUrl: "",
            sex: "",
            categoryLabel: "",
          }
        : {
            id: `${Date.now()}-placeholder`,
            participantType: "placeholder" as const,
            userId: null,
            participantName: "",
            participantCpf: "",
            avatarUrl: "",
            sex: "",
            categoryLabel: "",
          };
  }

  async function addInitialParticipant() {
    const nextParticipant = buildInitialParticipantDraft();
    if (!nextParticipant) return;

    if (mode === "edit" && initialMatch && onAddParticipant) {
      setSaving(true);
      try {
        await onAddParticipant(initialMatch, {
          userId: nextParticipant.userId,
          participantType: nextParticipant.participantType,
          participantName: nextParticipant.participantName,
          participantCpf: nextParticipant.participantCpf,
        });
        resetParticipantDraft();
      } finally {
        setSaving(false);
      }
      return;
    }

    setInitialParticipants((current) => [...current, nextParticipant]);
    resetParticipantDraft();
  }

  function removeInitialParticipant(id: string) {
    setInitialParticipants((current) => current.filter((item) => item.id !== id));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!clubId || !selectedClub) {
      setToastVariant("error");
      setToastMessage(texts.form.selectClub);
      setClubResultsOpen(true);
      return;
    }
    if (!date) {
      setToastVariant("error");
      setToastMessage(texts.form.dateRequired);
      return;
    }
    if (categories.length === 0) {
      setToastVariant("error");
      setToastMessage(texts.form.categoriesHint);
      return;
    }
    if (
      normalizedLeftPlayersCount + normalizedRightPlayersCount >
      Number(totalPlayers || 0)
    ) {
      setToastVariant("error");
      setToastMessage(texts.form.sidePlayersTotalError);
      return;
    }

    setSaving(true);
    try {
      const priceCents = Math.round(Number(totalPrice || 0) * 100);
      const effectiveTotalPriceCents =
        priceMode === "player"
          ? Math.max(0, priceCents) * Number(totalPlayers)
          : Math.max(0, priceCents);
      await onSubmit({
        clubId: Number(clubId),
        title,
        date,
        startTime,
        endTime,
        totalPlayers: Number(totalPlayers),
        categories,
        genderPreference,
        sidePreference: "any",
        leftPlayersCount: normalizedLeftPlayersCount,
        rightPlayersCount: normalizedRightPlayersCount,
        totalPriceCents: effectiveTotalPriceCents,
        priceCents: Math.max(0, priceCents),
        priceMode,
        paymentMode,
        paymentInstructions: "",
        description: details,
        notes: "",
        organizerPlays,
        approvalRequired,
        responsibilityAccepted,
        initialParticipants:
          mode === "edit" && onAddParticipant
            ? []
            : initialParticipants.map((item) => ({
                userId: item.userId,
                participantType: item.participantType,
                participantName: item.participantName,
                participantCpf: item.participantCpf,
              })),
      });
      setToastVariant("success");
      setToastMessage(mode === "edit" ? texts.form.editSuccess : texts.form.success);
      onClose();
    } catch (err) {
      setToastVariant("error");
      setToastMessage(
        err instanceof Error
          ? err.message
          : mode === "edit"
            ? texts.form.editError
            : texts.form.saveError,
      );
    } finally {
      setSaving(false);
    }
  }


  async function handleCancelMatch() {
    if (!onCancelMatch || canceling || saving) return;
    setCanceling(true);
    try {
      await onCancelMatch();
    } catch (err) {
      setToastVariant("error");
      setToastMessage(err instanceof Error ? err.message : texts.form.cancelMatchError);
    } finally {
      setCanceling(false);
    }
  }

  if (!open) {
    return toastMessage ? (
      <Toast
        message={toastMessage}
        variant={toastVariant}
        onClose={() => setToastMessage("")}
      />
    ) : null;
  }

  const modal = (
    <>
      <div
        className="match-create-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label={texts.form.title}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !controlsDisabled) onClose();
        }}
      >
        <section className="match-create-panel" aria-label={texts.form.title}>
        <div className="match-create-panel__header">
          <div>
            <span className="match-create-panel__eyebrow">{texts.title}</span>
            <h2>{mode === "edit" ? texts.form.editTitle : texts.form.title}</h2>
            <p>{mode === "edit" ? texts.form.editSubtitle : texts.form.subtitle}</p>
          </div>
          <div className="match-create-panel__header-actions">
            {mode === "edit" ? (
              <div className="match-create-panel__estimate">
                <small>{siteTexts.eventDetails.amountPerPlayer}</small>
                <strong>{estimatedPerPlayer}</strong>
              </div>
            ) : null}
            <button
              type="button"
              className="match-create-panel__close"
              onClick={onClose}
              aria-label={texts.actions.closeModal}
              disabled={controlsDisabled}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>
          </div>
        </div>

        <form className="match-create-panel__form" onSubmit={handleSubmit}>
          <label className="matches-field">
            <span>{texts.form.club}</span>
            <input
              type="search"
              value={clubSearch}
              onChange={(event) => {
                setClubSearch(event.target.value);
                setClubId("");
                setClubResultsOpen(true);
              }}
              onFocus={() => setClubResultsOpen(true)}
              placeholder={texts.form.clubSearchPlaceholder}
              role="combobox"
              aria-expanded={clubResultsOpen}
              aria-controls="match-club-results"
              aria-autocomplete="list"
              required
              disabled={loadingClubs || controlsDisabled}
            />
          </label>
          <div
            id="match-club-results"
            className={`match-club-results${clubResultsOpen ? " is-open" : ""}`}
            role="listbox"
          >
            {loadingClubs ? (
              <p>{siteTexts.common.loading}</p>
            ) : filteredClubs.length === 0 ? (
              <p>{texts.form.clubNoResults}</p>
            ) : (
              filteredClubs.map((club) => {
                const selected = String(club.id) === clubId;
                const location = [club.neighborhood, club.city, club.state]
                  .map((item) => String(item || "").trim())
                  .filter(Boolean)
                  .join(" - ");
                return (
                  <button
                    key={club.id}
                    type="button"
                    className={`match-club-result${selected ? " is-selected" : ""}`}
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setClubId(String(club.id));
                      setClubSearch(club.name);
                      setClubResultsOpen(false);
                    }}
                    disabled={controlsDisabled}
                  >
                    {club.logoUrl ? (
                      <img src={club.logoUrl} alt="" />
                    ) : (
                      <span
                        className="match-club-result__icon material-symbols-outlined"
                        aria-hidden="true"
                      >
                        sports_tennis
                      </span>
                    )}
                    <span>
                      <strong>{club.name}</strong>
                      <small>{location || club.address}</small>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <label className="matches-field">
            <span>{texts.form.matchTitle}</span>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={texts.form.matchTitlePlaceholder}
              disabled={controlsDisabled}
            />
          </label>

          <div className="match-create-panel__grid">
            <OrganizerDatePicker
              label={texts.form.date}
              value={date}
              onChange={setDate}
            />
            <div className="matches-field">
              <span>{texts.form.startTime}</span>
              <OrganizerTimeInput
                label={texts.form.startTime}
                className="matches-time-input"
                hideLabel
                value={startTime}
                onChange={setStartTime}
                required
                disabled={controlsDisabled}
              />
            </div>
            <div className="matches-field">
              <span>{texts.form.endTime}</span>
              <OrganizerTimeInput
                label={texts.form.endTime}
                className="matches-time-input"
                hideLabel
                value={endTime}
                onChange={setEndTime}
                required
                disabled={controlsDisabled}
              />
            </div>
            <div
              className="matches-field match-create-panel__duration"
              role="status"
              aria-live="polite"
            >
              <span>{texts.form.duration}</span>
              <div
                className={`match-create-panel__duration-value${calculatedDuration.invalid ? " is-invalid" : ""}`}
              >
                {calculatedDuration.label}
              </div>
            </div>
            <label className="matches-field">
              <span>{texts.form.totalPlayers}</span>
              <input
                type="number"
                min="2"
                max="12"
                step="1"
                value={totalPlayers}
                onChange={(event) => setTotalPlayers(event.target.value)}
                required
                disabled={controlsDisabled}
              />
            </label>
          </div>

          <fieldset className="match-create-panel__participants">
            <div className="match-create-panel__participants-header">
              <div>
                <legend>
                  {mode === "edit"
                    ? texts.form.editParticipants
                    : texts.form.initialParticipants}
                </legend>
                <p>
                  {mode === "edit"
                    ? texts.form.editParticipantsHint
                    : texts.form.initialParticipantsHint}
                </p>
              </div>
            </div>

            <div className="match-create-panel__participant-picker">
              <label className="matches-field">
                <span>{texts.form.participantType}</span>
                <select
                  value={participantTypeDraft}
                  onChange={(event) => {
                    setParticipantTypeDraft(
                      event.target.value as InitialParticipantDraft["participantType"],
                    );
                    setParticipantUserIdDraft("");
                  }}
                  disabled={controlsDisabled}
                >
                  <option value="registered">
                    {texts.form.participantRegistered}
                  </option>
                  <option value="external">{texts.form.participantExternal}</option>
                  <option value="placeholder">
                    {texts.form.participantPlaceholder}
                  </option>
                </select>
              </label>

              {participantTypeDraft === "registered" ? (
                <>
                  <label className="matches-field">
                    <span>{texts.form.participantSearch}</span>
                    <input
                      type="search"
                      value={participantSearch}
                      onChange={(event) => {
                        setParticipantSearch(event.target.value);
                        setParticipantUserIdDraft("");
                      }}
                      placeholder={texts.form.participantSearchPlaceholder}
                      disabled={controlsDisabled}
                    />
                  </label>
                  <div className="match-athlete-results" role="listbox">
                    {availableParticipantOptions.length === 0 ? (
                      <p>{texts.form.participantNoResults}</p>
                    ) : (
                      availableParticipantOptions.map((participant) => {
                        const selected =
                          String(participant.id) === participantUserIdDraft;
                        return (
                          <button
                            key={participant.id}
                            type="button"
                            className={`match-athlete-result${selected ? " is-selected" : ""}`}
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              setParticipantUserIdDraft(String(participant.id));
                              setParticipantSearch(participant.displayName);
                            }}
                            disabled={controlsDisabled}
                          >
                            <UserAvatar
                              name={participant.displayName}
                              avatarUrl={participant.avatarUrl || undefined}
                              sex={participant.sex || undefined}
                              size={36}
                            />
                            <span>
                              <strong>{participant.displayName}</strong>
                              <small>
                                {participant.categoryLabel ||
                                  texts.form.participantRegistered}
                              </small>
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              ) : participantTypeDraft === "external" ? (
                <>
                  <label className="matches-field">
                    <span>{texts.form.participantName}</span>
                    <input
                      type="text"
                      value={participantNameDraft}
                      onChange={(event) => setParticipantNameDraft(event.target.value)}
                      disabled={controlsDisabled}
                    />
                  </label>
                  <label className="matches-field">
                    <span>{texts.form.participantCpf}</span>
                    <input
                      type="text"
                      value={participantCpfDraft}
                      onChange={(event) => setParticipantCpfDraft(event.target.value)}
                      disabled={controlsDisabled}
                    />
                  </label>
                </>
              ) : null}

              <Button
                type="button"
                variant="pill"
                onClick={addInitialParticipant}
                disabled={addParticipantDisabled}
              >
                {texts.form.addInitialParticipant}
              </Button>
            </div>

            {participantTypeDraft === "registered" && selectedParticipantOption ? (
              <div className="match-create-panel__participant-preview">
                <UserAvatar
                  name={selectedParticipantOption.displayName}
                  avatarUrl={selectedParticipantOption.avatarUrl || undefined}
                  sex={selectedParticipantOption.sex || undefined}
                  size={44}
                />
                <div>
                  <strong>{selectedParticipantOption.displayName}</strong>
                  <span>
                    {selectedParticipantOption.categoryLabel ||
                      texts.form.participantRegistered}
                  </span>
                </div>
              </div>
            ) : participantTypeDraft !== "registered" ? (
              <div className="match-create-panel__participant-preview">
                <UserAvatar
                  name={participantNameDraft || texts.form.participantPlaceholder}
                  size={44}
                />
                <div>
                  <strong>
                    {participantTypeDraft === "external" && participantNameDraft.trim()
                      ? participantNameDraft.trim()
                      : texts.form.participantPlaceholder}
                  </strong>
                  <span>{texts.form.participantNotRegistered}</span>
                </div>
              </div>
            ) : null}

            {existingParticipants.length === 0 && initialParticipants.length === 0 ? (
              <p>{texts.form.noInitialParticipants}</p>
            ) : (
              <div className="match-create-panel__selected-participants">
                {existingParticipants.map((participant) => (
                  <article
                    key={`existing-${participant.id}`}
                    className={`match-create-panel__selected-participant match-create-panel__selected-participant--existing${
                      onReviewParticipant &&
                      participant.participantId &&
                      ["pending", "invited"].includes(participant.status)
                        ? " has-confirm-action"
                        : ""
                    }`}
                  >
                    <UserAvatar
                      name={participant.name}
                      avatarUrl={participant.avatarUrl || undefined}
                      sex={participant.sex || undefined}
                      size={44}
                    />
                    <div>
                      <div className="match-create-panel__participant-identity">
                        <strong>{participant.name}</strong>
                        <ParticipantStatusIcon
                          status={participant.status}
                          label={participant.meta}
                        />
                      </div>
                    </div>
                    {onReviewParticipant &&
                    participant.participantId &&
                    ["pending", "invited"].includes(participant.status) ? (
                      <div className="match-create-panel__participant-actions">
                        {participant.status === "pending" ? (
                          <button
                            type="button"
                            className="match-create-panel__participant-action match-create-panel__participant-action--confirm"
                            onClick={() =>
                              void onReviewParticipant(
                                participant.participantId || 0,
                                "confirmed",
                              )
                            }
                            disabled={
                              controlsDisabled ||
                              reviewingParticipantId ===
                                participant.participantId
                            }
                            aria-label={texts.detail.approveParticipant}
                            title={texts.detail.approveParticipant}
                          >
                            <span
                              className="material-symbols-outlined"
                              aria-hidden="true"
                            >
                              check
                            </span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="match-create-panel__participant-action match-create-panel__participant-action--decline"
                          onClick={() =>
                            void onReviewParticipant(
                              participant.participantId || 0,
                              "declined",
                            )
                          }
                          disabled={
                            controlsDisabled ||
                            reviewingParticipantId === participant.participantId
                          }
                          aria-label={texts.detail.declineParticipant}
                          title={texts.detail.declineParticipant}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            close
                          </span>
                        </button>
                      </div>
                    ) : null}
                  </article>
                ))}
                {initialParticipants.map((participant) => (
                  <article
                    key={participant.id}
                    className="match-create-panel__selected-participant"
                  >
                    <UserAvatar
                      name={
                        participant.participantName ||
                        texts.form.participantPlaceholder
                      }
                      avatarUrl={participant.avatarUrl || undefined}
                      sex={participant.sex || undefined}
                      size={44}
                    />
                    <div>
                      <strong>
                        {participant.participantName ||
                          texts.form.participantPlaceholder}
                      </strong>
                      <span>
                        {participant.userId
                          ? participant.categoryLabel ||
                            texts.form.participantRegistered
                          : texts.form.participantNotRegistered}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="match-create-panel__participant-remove"
                      onClick={() => removeInitialParticipant(participant.id)}
                      disabled={controlsDisabled}
                      aria-label={texts.form.removeInitialParticipant}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        close
                      </span>
                    </button>
                  </article>
                ))}
              </div>
            )}
          </fieldset>

          <fieldset className="match-create-panel__categories">
            <legend>{texts.form.categories}</legend>
            <p>{texts.form.categoriesHint}</p>
            <div className="match-create-panel__category-list">
              {CATEGORY_VALUES.map((category) => {
                const active = categories.includes(category);
                return (
                  <button
                    key={category}
                    type="button"
                    className={`match-category-chip${active ? " is-active" : ""}`}
                    onClick={() => toggleCategory(category)}
                  >
                    {categoryLabel(category)}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="match-create-panel__grid">
            <label className="matches-field">
              <span>{texts.form.genderPreference}</span>
              <select
                value={genderPreference}
                onChange={(event) =>
                  setGenderPreference(
                    event.target.value as CreateOpenMatchPayload["genderPreference"],
                  )
                }
                disabled={controlsDisabled}
              >
                <option value="any">{texts.options.genderAny}</option>
                <option value="female">{texts.options.genderFemale}</option>
                <option value="male">{texts.options.genderMale}</option>
                <option value="mixed">{texts.options.genderMixed}</option>
              </select>
            </label>

            <label className="matches-field">
              <span>{texts.form.leftPlayersCount}</span>
              <input
                type="number"
                min="0"
                max={totalPlayers}
                step="1"
                value={leftPlayersCount}
                onChange={(event) => setLeftPlayersCount(event.target.value)}
                disabled={controlsDisabled}
              />
            </label>

            <label className="matches-field">
              <span>{texts.form.rightPlayersCount}</span>
              <input
                type="number"
                min="0"
                max={totalPlayers}
                step="1"
                value={rightPlayersCount}
                onChange={(event) => setRightPlayersCount(event.target.value)}
                disabled={controlsDisabled}
              />
            </label>

            <div className="matches-field">
              <span>{texts.form.priceMode}</span>
              <div className="status-filter-buttons" role="group">
                <Button
                  type="button"
                  variant="pill"
                  className={priceMode === "court" ? "active" : ""}
                  onClick={() => setPriceMode("court")}
                  disabled={controlsDisabled}
                >
                  {texts.options.priceModeCourt}
                </Button>
                <Button
                  type="button"
                  variant="pill"
                  className={priceMode === "player" ? "active" : ""}
                  onClick={() => setPriceMode("player")}
                  disabled={controlsDisabled}
                >
                  {texts.options.priceModePlayer}
                </Button>
              </div>
            </div>

            <div className="matches-field">
              <label htmlFor="match-total-price">
                {priceMode === "player"
                  ? texts.form.playerPrice
                  : texts.form.totalPrice}
              </label>
              <input
                id="match-total-price"
                type="number"
                min="0"
                step="0.01"
                value={totalPrice}
                onChange={(event) => setTotalPrice(event.target.value)}
                placeholder={
                  priceMode === "player"
                    ? texts.form.playerPricePlaceholder
                    : texts.form.totalPricePlaceholder
                }
                aria-describedby="match-total-price-hint"
                disabled={controlsDisabled}
              />
              <small id="match-total-price-hint" className="matches-field__hint">
                {priceMode === "player"
                  ? texts.form.playerPriceHint
                  : texts.form.totalPriceHint}
              </small>
            </div>

            <label className="matches-field">
              <span>{texts.form.paymentMode}</span>
              <select
                value={paymentMode}
                onChange={(event) =>
                  setPaymentMode(
                    event.target.value as CreateOpenMatchPayload["paymentMode"],
                  )
                }
                disabled={controlsDisabled}
              >
                <option value="organizer">{texts.options.paymentOrganizer}</option>
                <option value="club">{texts.options.paymentClub}</option>
                <option value="onsite">{texts.options.paymentOnsite}</option>
                <option value="pix">{texts.options.paymentPix}</option>
                <option value="custom">{texts.options.paymentCustom}</option>
                <option value="free">{texts.options.paymentFree}</option>
                <option value="undefined">{texts.options.paymentUndefined}</option>
              </select>
            </label>
          </div>

          <label className="matches-field">
            <span>{texts.form.details}</span>
            <textarea
              rows={5}
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              placeholder={texts.form.detailsPlaceholder}
              disabled={controlsDisabled}
            />
          </label>

          <div className="match-create-panel__toggles">
            <label className="matches-checkbox">
              <input
                type="checkbox"
                checked={organizerPlays}
                onChange={(event) => setOrganizerPlays(event.target.checked)}
                disabled={controlsDisabled}
              />
              <span>{texts.form.organizerPlays}</span>
            </label>
            <label className="matches-checkbox">
              <input
                type="checkbox"
                checked={approvalRequired}
                onChange={(event) => setApprovalRequired(event.target.checked)}
                disabled={controlsDisabled}
              />
              <span>{texts.form.approvalRequired}</span>
            </label>
          </div>

          <label className="matches-checkbox matches-checkbox--responsibility">
            <input
              type="checkbox"
              checked={responsibilityAccepted}
              onChange={(event) => setResponsibilityAccepted(event.target.checked)}
              disabled={controlsDisabled}
              required
            />
            <span>{texts.form.responsibility}</span>
          </label>

          <div className="match-create-panel__actions">
            {mode === "edit" && onCancelMatch ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => void handleCancelMatch()}
                disabled={controlsDisabled}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  event_busy
                </span>
                {canceling ? texts.form.cancelMatchSubmitting : texts.form.cancelMatch}
              </Button>
            ) : null}
            <Button type="submit" disabled={controlsDisabled || loadingClubs}>
              {saving
                ? mode === "edit"
                  ? texts.form.editSubmitting
                  : texts.form.submitting
                : mode === "edit"
                  ? texts.form.editSubmit
                  : texts.form.submit}
            </Button>
          </div>
        </form>
        </section>
      </div>

      {toastMessage ? (
        <Toast
          message={toastMessage}
          variant={toastVariant}
          onClose={() => setToastMessage("")}
        />
      ) : null}
    </>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
